import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "@/context/LanguageContext";
import Landing from "@/pages/Landing";
import LevelTest from "@/pages/LevelTest";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Privacy from "@/pages/Privacy";
import AdminLogin from "@/pages/AdminLogin";
import AdminLeads from "@/pages/AdminLeads";
import AdminForgotPassword from "@/pages/AdminForgotPassword";
import AdminResetPassword from "@/pages/AdminResetPassword";
import Unsubscribe from "@/pages/Unsubscribe";
import ConfirmBooking from "@/pages/ConfirmBooking";
import LegacyRedirect, { LEGACY_MAP } from "@/pages/LegacyRedirect";
import AulaGratis from "@/pages/AulaGratis";
import BioLink from "@/pages/BioLink";
import ParisEm10Frases from "@/pages/ParisEm10Frases";
import ProfLionel from "@/pages/ProfLionel";
import ProgrammaticPage from "@/pages/ProgrammaticPage";
import PillarPage from "@/pages/PillarPage";
import { PILLAR_KEYS } from "@/lib/pillarPages";
import VoiceIntroWidget from "@/components/landing/VoiceIntroWidget";
import TopRibbon from "@/components/landing/TopRibbon";
import { Toaster } from "@/components/ui/sonner";

const RibbonSlot = () => {
  const { pathname } = useLocation();
  // Hide on internal/admin routes AND on standalone conversion/bio pages
  // (they must stay distraction-free for ad/social traffic).
  const hidden =
    pathname.startsWith("/admin") ||
    pathname.startsWith("/confirmar-agendamento") ||
    pathname === "/aula-gratis" ||
    pathname === "/paris-em-10-frases" ||
    pathname === "/l";
  if (hidden) return null;
  return <TopRibbon />;
};

// Adds a top padding (44px mobile / 48px desktop) matching the TopRibbon height
// so the fixed ribbon doesn't cover the first line of public pages that use an
// inline (non-fixed) header. Landing's fixed Header is offset via `top-11 sm:top-12`.
const PublicShell = ({ children }) => {
  const { pathname } = useLocation();
  const isLanding = pathname === "/";
  const noShell =
    pathname.startsWith("/admin") ||
    pathname.startsWith("/confirmar-agendamento") ||
    pathname === "/aula-gratis" ||
    pathname === "/paris-em-10-frases" ||
    pathname === "/l";
  if (isLanding || noShell) return children;
  return <div className="pt-11 sm:pt-12">{children}</div>;
};

function App() {
  return (
    <LanguageProvider>
      <div className="App">
        <BrowserRouter>
          <RibbonSlot />
          <PublicShell>
            <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/teste-de-nivel" element={<LevelTest />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/privacidade" element={<Privacy />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin/leads" element={<AdminLeads />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/confirmar-agendamento" element={<ConfirmBooking />} />
            <Route path="/aula-gratis" element={<AulaGratis />} />
            <Route path="/paris-em-10-frases" element={<ParisEm10Frases />} />
            <Route path="/prof-lionel" element={<ProfLionel />} />
            {/* Programmatic SEO routes — dynamic long-tail pages backed by
                /lib/programmaticPages.js (data-driven, no hard-coded content). */}
            <Route path="/frances/como-dizer/:slug" element={<ProgrammaticPage type="how" />} />
            <Route path="/frances/pronuncia/:slug" element={<ProgrammaticPage type="pron" />} />
            <Route path="/frances/diferenca/:slug" element={<ProgrammaticPage type="diff" />} />
            {/* Pillar pages — topical hubs that concentrate PageRank on /aula-gratis */}
            {PILLAR_KEYS.map((s) => (
              <Route key={s} path={`/${s}`} element={<PillarPage slug={s} />} />
            ))}
            <Route path="/l" element={<BioLink />} />
            {/* Legacy WordPress-era URLs — 301-style redirects for SEO recovery.
                Also declared with trailing-slash aliases because Google indexed those. */}
            {Object.entries(LEGACY_MAP).flatMap(([from, to]) => [
              <Route key={from} path={from} element={<LegacyRedirect to={to} />} />,
              <Route key={`${from}/`} path={`${from}/`} element={<LegacyRedirect to={to} />} />,
            ])}
          </Routes>
          </PublicShell>
          <VoiceIntroWidget />
        </BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
      </div>
    </LanguageProvider>
  );
}

export default App;
